import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import getAPIUrl from "./API_FIX";
import NumericKeypad from "./NumericKeypad";
import TapPad from "./TapPad";
import ScorecardScan from "./ScorecardScan";
import useScoreEntryNavigation from "../hooks/useScoreEntryNavigation";

// EXPERIMENTAL scorecard OCR, off unless explicitly enabled (build env, or a
// per-device localStorage flag for testing). Server endpoint is disabled too.
const OCR_ENABLED =
  process.env.REACT_APP_ENABLE_SCORECARD_OCR === "true" ||
  (typeof window !== "undefined" &&
    window.localStorage.getItem("scorecardOcr") === "on");

/** True on phone-sized viewports; drives the touch keypad + sticky save bar. */
function useIsMobile() {
  const query = "(max-width: 640px)";
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(query).matches
      : false
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

// Per-field maximums (all match types are 10 shots/stage → 100 pts, ≤10 X).
const FIELD_MAX = { score: 100, x_count: 10 };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = getAPIUrl(BACKEND_URL);

/**
 * Score entry keyboard strategy (deliberate, do not change lightly):
 *
 * 1. Field order is linear in sheet order:
 *    for each match-type × caliber × stage: [Score] then [X]
 * 2. Tab / Shift+Tab: browser default within that order (explicit tabIndex).
 * 3. Enter: move to the next score field (same as Tab forward). Never submits.
 * 4. Focus: select-all so overwrite is one keystroke.
 * 5. Submit only via the "Save All Scores" button (type=submit).
 *
 * Empty score/X stays null (skipped stage), not 0 — do not coerce "" to 0.
 */
const ScoreEntry = () => {
  const { matchId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const formRootRef = useRef(null);
  const isMobile = useIsMobile();
  // The score/X cell currently targeted by the on-screen keypad (mobile only).
  const [activeField, setActiveField] = useState(null);
  // A/B test for mobile entry: "keypad" (type the total) vs "tap" (shot-by-shot,
  // auto-totals). Randomly assigned on first use, then remembered; either pad
  // can switch to the other so a tester can compare them on the same match.
  const [entryMode, setEntryMode] = useState(() => {
    if (typeof window === "undefined") return "keypad";
    const saved = window.localStorage.getItem("scoreEntryMode");
    if (saved === "keypad" || saved === "tap") return saved;
    const assigned = Math.random() < 0.5 ? "keypad" : "tap";
    window.localStorage.setItem("scoreEntryMode", assigned);
    return assigned;
  });
  const setMode = useCallback((mode) => {
    setEntryMode(mode);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("scoreEntryMode", mode);
    }
  }, []);
  // Tap-mode shot strings, keyed `${scoreIndex}_${stageIndex}` → [{val,isX}].
  const [shotsByStage, setShotsByStage] = useState({});

  const [match, setMatch] = useState(null);
  const [matchConfig, setMatchConfig] = useState(null);
  const [allShooters, setAllShooters] = useState([]);
  const [rosterIds, setRosterIds] = useState([]);
  const [showAllShooters, setShowAllShooters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    shooter_id: searchParams.get("shooter") || "",
    match_id: matchId,
    scores: [],
  });

  // Field advancement (keyboard + keypad + tap) is driven by an explicit
  // ordered field list derived from formData, not by scraping the DOM.
  const {
    focusNextFromElement,
    advanceFromActive,
    advanceToNextStage,
  } = useScoreEntryNavigation({
    scores: formData.scores,
    matchConfig,
    formRootRef,
    activeField,
    setActiveField,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("Authentication required. Please log in again.");
          setLoading(false);
          return;
        }

        const config = {
          headers: { Authorization: `Bearer ${token}` },
        };

        const [matchResponse, configResponse, shootersResponse, rosterResponse] =
          await Promise.all([
            axios.get(`${API}/matches/${matchId}`, config),
            axios.get(`${API}/match-config/${matchId}`, config),
            axios.get(`${API}/shooters`, config),
            axios.get(`${API}/matches/${matchId}/roster`, config).catch(() => null),
          ]);

        setMatch(matchResponse.data);
        setMatchConfig(configResponse.data);
        setAllShooters(shootersResponse.data || []);

        const members = rosterResponse?.data?.members || [];
        const ids = members.map((m) => m.shooter.id);
        setRosterIds(ids);
        // If roster is empty, show full directory by default
        setShowAllShooters(ids.length === 0);

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load required data. Please try again.");
        setLoading(false);
      }
    };

    fetchData();
  }, [matchId]);

  // Build empty stage rows from match config
  const buildEmptyScores = useCallback((config) => {
    const initialScores = [];
    config.match_types.forEach((matchType) => {
      matchType.calibers.forEach((caliber) => {
        const stages = matchType.entry_stages.map((stageName) => ({
          name: stageName,
          score: null,
          x_count: null,
        }));
        initialScores.push({
          match_type_instance: matchType.instance_name,
          caliber: caliber,
          stages: stages,
          fired_twentytwo: false,
          existing_id: null,
        });
      });
    });
    return initialScores;
  }, []);

  // Initialize score forms when shooter is selected
  useEffect(() => {
    if (!matchConfig || !formData.shooter_id || matchConfig.match_types.length === 0) {
      return;
    }

    let cancelled = false;

    const fetchExistingScores = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const config = {
          headers: { Authorization: `Bearer ${token}` },
        };

        const scoreResponse = await axios.get(
          `${API}/scores?shooter_id=${formData.shooter_id}&match_id=${matchId}`,
          config
        );
        if (cancelled) return;

        const existingScores = scoreResponse.data;
        const existingScoreMap = {};
        existingScores.forEach((score) => {
          const key = `${score.match_type_instance}-${score.caliber}`;
          existingScoreMap[key] = score;
        });

        const initialScores = [];
        matchConfig.match_types.forEach((matchType) => {
          matchType.calibers.forEach((caliber) => {
            const key = `${matchType.instance_name}-${caliber}`;
            if (existingScoreMap[key]) {
              initialScores.push({
                match_type_instance: matchType.instance_name,
                caliber: caliber,
                stages: existingScoreMap[key].stages,
                fired_twentytwo: existingScoreMap[key].fired_twentytwo || false,
                // Remember the existing doc id so save updates in place (PUT)
                // instead of creating a duplicate score record (POST).
                existing_id: existingScoreMap[key].id || null,
              });
            } else {
              const stages = matchType.entry_stages.map((stageName) => ({
                name: stageName,
                score: null,
                x_count: null,
              }));
              initialScores.push({
                match_type_instance: matchType.instance_name,
                caliber: caliber,
                stages: stages,
                fired_twentytwo: false,
                existing_id: null,
              });
            }
          });
        });

        setFormData((prev) => ({
          ...prev,
          scores: initialScores,
        }));
      } catch (err) {
        console.error("Error fetching existing scores:", err);
        if (cancelled) return;
        setFormData((prev) => ({
          ...prev,
          scores: buildEmptyScores(matchConfig),
        }));
      }
    };

    fetchExistingScores();
    return () => {
      cancelled = true;
    };
    // Only re-run when shooter or config identity changes — not when scores mutate
  }, [formData.shooter_id, matchConfig, matchId, buildEmptyScores]);

  const shootersForSelect = useMemo(() => {
    if (showAllShooters || rosterIds.length === 0) {
      return allShooters;
    }
    const set = new Set(rosterIds);
    const rostered = allShooters.filter((s) => set.has(s.id));
    // If current selection is outside roster, still include them so the select stays valid
    if (
      formData.shooter_id &&
      !set.has(formData.shooter_id) &&
      !rostered.some((s) => s.id === formData.shooter_id)
    ) {
      const extra = allShooters.find((s) => s.id === formData.shooter_id);
      if (extra) return [extra, ...rostered];
    }
    return rostered;
  }, [allShooters, rosterIds, showAllShooters, formData.shooter_id]);

  const handleShooterChange = (e) => {
    setFormData({
      shooter_id: e.target.value,
      match_id: matchId,
      scores: [],
    });
    setActiveField(null);
    setError(null);
  };

  const handleStageChange = (scoreIndex, stageIndex, field, value) => {
    setFormData((prev) => {
      const updatedScores = prev.scores.map((s, i) => {
        if (i !== scoreIndex) return s;
        const stages = s.stages.map((st, j) => {
          if (j !== stageIndex) return st;
          if (value === "") {
            return { ...st, [field]: null };
          }
          const parsed = parseInt(value, 10);
          if (Number.isNaN(parsed)) {
            return { ...st, [field]: null };
          }
          return { ...st, [field]: parsed };
        });
        return { ...s, stages };
      });
      return { ...prev, scores: updatedScores };
    });
  };

  const handleFiredTwentytwoChange = (scoreIndex, checked) => {
    setFormData((prev) => ({
      ...prev,
      scores: prev.scores.map((s, i) =>
        i === scoreIndex ? { ...s, fired_twentytwo: checked } : s
      ),
    }));
  };

  const handleScoreKeyDown = (e) => {
    // Enter advances like Tab; never submit the form from a field
    if (e.key === "Enter") {
      e.preventDefault();
      focusNextFromElement(e.target);
      return;
    }

    // Escape clears the current field (sets null) without leaving
    if (e.key === "Escape") {
      e.preventDefault();
      e.target.value = "";
      e.target.dispatchEvent(new Event("input", { bubbles: true }));
      // onChange may not fire from programmatic value set — call path via dataset
      const scoreIndex = parseInt(e.target.dataset.scoreIndex, 10);
      const stageIndex = parseInt(e.target.dataset.stageIndex, 10);
      const field = e.target.dataset.field;
      if (!Number.isNaN(scoreIndex) && !Number.isNaN(stageIndex) && field) {
        handleStageChange(scoreIndex, stageIndex, field, "");
      }
    }
  };

  const handleScoreFocus = (e) => {
    if (typeof e.target.select === "function") {
      e.target.select();
    }
    // Track the focused cell so the mobile keypad knows what it edits.
    const scoreIndex = parseInt(e.target.dataset.scoreIndex, 10);
    const stageIndex = parseInt(e.target.dataset.stageIndex, 10);
    const field = e.target.dataset.field;
    if (!Number.isNaN(scoreIndex) && !Number.isNaN(stageIndex) && field) {
      setActiveField({ scoreIndex, stageIndex, field });
    }
  };

  // --- Mobile on-screen keypad -------------------------------------------
  const activeStageValue = () => {
    if (!activeField) return null;
    const s = formData.scores[activeField.scoreIndex];
    const stage = s?.stages?.[activeField.stageIndex];
    if (!stage) return null;
    return stage[activeField.field];
  };

  const commitActive = (nextValue) => {
    if (!activeField) return;
    handleStageChange(
      activeField.scoreIndex,
      activeField.stageIndex,
      activeField.field,
      nextValue === null ? "" : String(nextValue)
    );
  };

  const keypadDigit = (d) => {
    if (!activeField) return;
    const max = FIELD_MAX[activeField.field] ?? 100;
    const cur = activeStageValue();
    const curStr = cur === null || cur === undefined ? "" : String(cur);
    let candidate = parseInt(curStr + d, 10);
    if (Number.isNaN(candidate) || candidate > max) candidate = parseInt(d, 10);
    commitActive(candidate);

    // Conservative auto-advance: once the field can't grow further.
    if (activeField.field === "x_count") {
      // Only "1" can extend to "10"; everything else is terminal.
      if (candidate !== 1 || candidate === max) advanceFromActive();
    } else if (candidate === max || (curStr + d).length >= 3) {
      advanceFromActive();
    }
  };

  const keypadBackspace = () => {
    if (!activeField) return;
    const cur = activeStageValue();
    const curStr = cur === null || cur === undefined ? "" : String(cur);
    const nextStr = curStr.slice(0, -1);
    commitActive(nextStr === "" ? null : parseInt(nextStr, 10));
  };

  const keypadQuick = () => {
    if (!activeField) return;
    commitActive(FIELD_MAX[activeField.field] ?? 100);
    advanceFromActive();
  };

  // Context line for the keypad readout: match/caliber and stage, without the
  // field name (the keypad shows that beside the live value on its own line).
  const keypadContext = () => {
    if (!activeField) return "";
    const s = formData.scores[activeField.scoreIndex];
    const stage = s?.stages?.[activeField.stageIndex];
    return [
      `${s?.match_type_instance ?? ""} ${s?.caliber ?? ""}`.trim(),
      stage?.name ?? "",
    ]
      .filter(Boolean)
      .join(" · ");
  };

  // --- Tap mode (shot-by-shot) -------------------------------------------
  const commitStage = (score, x) => {
    if (!activeField) return;
    commitField("score", score);
    commitField("x_count", x);
  };
  const commitField = (field, value) => {
    handleStageChange(
      activeField.scoreIndex,
      activeField.stageIndex,
      field,
      value === null ? "" : String(value)
    );
  };

  const stageShots = () =>
    activeField
      ? shotsByStage[`${activeField.scoreIndex}_${activeField.stageIndex}`] || []
      : [];

  const setStageShots = (shots) => {
    const key = `${activeField.scoreIndex}_${activeField.stageIndex}`;
    setShotsByStage((prev) => ({ ...prev, [key]: shots }));
    const total = shots.reduce((a, s) => a + s.val, 0);
    const x = shots.filter((s) => s.isX).length;
    commitStage(shots.length ? total : null, shots.length ? x : null);
  };

  const tapShot = (val, isX) => {
    if (!activeField) return;
    const cur = stageShots();
    if (cur.length >= 10) return;
    const next = [...cur, { val, isX }];
    setStageShots(next);
    if (next.length >= 10) advanceToNextStage();
  };

  const tapUndo = () => {
    if (!activeField) return;
    const cur = stageShots();
    if (cur.length === 0) return;
    setStageShots(cur.slice(0, -1));
  };

  // --- Scorecard OCR (experimental) --------------------------------------
  // Map a parsed scorecard onto the form. Matches by match-type instance +
  // caliber + stage name; leaves unmatched cells untouched. Returns the number
  // of stages that got a score, for the confirmation message. Nothing is
  // saved — the user still reviews and presses Save.
  const applyOcrResult = (data) => {
    if (!data || !Array.isArray(data.match_types)) return 0;
    let filled = 0;
    const scores = formData.scores.map((s) => ({
      ...s,
      stages: s.stages.map((st) => ({ ...st })),
    }));
    data.match_types.forEach((mt) => {
      const target = scores.find(
        (s) =>
          s.match_type_instance === mt.instance_name && s.caliber === mt.caliber
      );
      if (!target || !Array.isArray(mt.stages)) return;
      mt.stages.forEach((os) => {
        const stage = target.stages.find((st) => st.name === os.name);
        if (!stage) return;
        if (os.score !== null && os.score !== undefined && os.score !== "") {
          stage.score = Number(os.score);
          filled += 1;
        }
        if (os.x_count !== null && os.x_count !== undefined && os.x_count !== "") {
          stage.x_count = Number(os.x_count);
        }
      });
    });
    if (filled > 0) setFormData((prev) => ({ ...prev, scores }));
    return filled;
  };

  // Live grand total across every card (for the mobile sticky bar).
  const grandTotal = formData.scores.reduce(
    (acc, s) => {
      s.stages.forEach((st) => {
        if (st.score !== null && st.score !== undefined) acc.score += st.score;
        if (st.x_count !== null && st.x_count !== undefined) acc.x += st.x_count;
      });
      return acc;
    },
    { score: 0, x: 0 }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.shooter_id || formData.scores.length === 0) {
      setError("Please select a shooter and enter scores");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication required. Please log in again.");
        setSaving(false);
        return;
      }

      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      // Submit each score entry independently. When this card already had a
      // saved score (existing_id), PUT to update it in place — otherwise POST a
      // new one. This prevents duplicate score records when re-saving a shooter.
      const submissionPromises = formData.scores.map((scoreEntry) => {
        const payload = {
          shooter_id: formData.shooter_id,
          match_id: matchId,
          match_type_instance: scoreEntry.match_type_instance,
          caliber: scoreEntry.caliber,
          stages: scoreEntry.stages,
          fired_twentytwo:
            scoreEntry.caliber !== ".22" && !!scoreEntry.fired_twentytwo,
        };
        return scoreEntry.existing_id
          ? axios.put(`${API}/scores/${scoreEntry.existing_id}`, payload, config)
          : axios.post(`${API}/scores`, payload, config);
      });

      await Promise.all(submissionPromises);
      setSuccess(true);
      setTimeout(() => {
        navigate(`/matches/${matchId}`);
      }, 1500);
    } catch (err) {
      console.error("Error submitting scores:", err);
      setError(
        err.response?.data?.detail || "Failed to save scores. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page text-center text-ink-500">Loading form data...</div>
    );
  }
  if (error && !match) {
    return (
      <div className="page text-center text-rose-600">{error}</div>
    );
  }
  if (!match || !matchConfig) {
    return (
      <div className="page text-center text-ink-500">Match data not found</div>
    );
  }

  if (success) {
    return (
      <div className="page">
        <div className="alert-success text-center">
          <h2 className="text-xl font-bold mb-2">Scores Saved Successfully!</h2>
          <p>Redirecting to match details...</p>
        </div>
      </div>
    );
  }

  // Group scores by match type for display (preserve formData.scores order)
  const scoresByType = {};
  if (formData.scores.length > 0) {
    formData.scores.forEach((score) => {
      const matchTypeObj = matchConfig.match_types.find(
        (mt) => mt.instance_name === score.match_type_instance
      );
      if (!matchTypeObj) return;

      const key = matchTypeObj.type;
      if (!scoresByType[key]) {
        scoresByType[key] = [];
      }
      scoresByType[key].push({
        ...score,
        matchTypeObj,
      });
    });
  }

  const calculateSubtotals = (score, matchTypeObj) => {
    const subtotals = {};

    if (
      matchTypeObj.subtotal_mappings &&
      Object.keys(matchTypeObj.subtotal_mappings).length > 0
    ) {
      for (const [subtotalName, sourceStages] of Object.entries(
        matchTypeObj.subtotal_mappings
      )) {
        let subtotalScore = 0;
        let subtotalXCount = 0;

        score.stages.forEach((stage) => {
          if (sourceStages.includes(stage.name)) {
            if (stage.score !== null) {
              subtotalScore += stage.score;
            }
            if (stage.x_count !== null) {
              subtotalXCount += stage.x_count;
            }
          }
        });

        subtotals[subtotalName] = {
          score: subtotalScore,
          x_count: subtotalXCount,
        };
      }
    }

    return subtotals;
  };

  const calculateTotals = (score) => {
    const allNull = score.stages.every((stage) => stage.score === null);

    if (allNull) {
      return {
        totalScore: null,
        totalXCount: null,
        allStagesTotalNull: true,
      };
    }

    return {
      totalScore: score.stages.reduce(
        (sum, stage) => (stage.score !== null ? sum + stage.score : sum),
        0
      ),
      totalXCount: score.stages.reduce(
        (sum, stage) => (stage.x_count !== null ? sum + stage.x_count : sum),
        0
      ),
      allStagesTotalNull: false,
    };
  };

  // Sequential tabIndex starting after shooter select (tabIndex 1)
  let navIndex = 2;

  return (
    <div className="page">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="page-title">Add Scores</h1>
          <p className="page-subtitle">
            Match: {match.name} ({new Date(match.date).toLocaleDateString()})
          </p>
          <p className="text-xs text-ink-400 mt-2">
            Keyboard: Tab / Enter moves Score → X → next stage. Esc clears field.
            Save only via the button.
          </p>
        </div>
        <Link to={`/matches/${matchId}`} className="btn-secondary btn-sm">
          Back to Match
        </Link>
      </div>

      {error && (
        <div className="alert-error mb-4" role="alert">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </div>
      )}

      <div className="card card-pad" ref={formRootRef}>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="mb-6">
            <label htmlFor="shooter_id" className="section-title block mb-2">
              Shooter
            </label>
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <select
                id="shooter_id"
                value={formData.shooter_id}
                onChange={handleShooterChange}
                tabIndex={1}
                className="select w-full md:w-1/2"
                required
              >
                <option value="">-- Select Shooter --</option>
                {shootersForSelect.map((shooter) => (
                  <option key={shooter.id} value={shooter.id}>
                    {shooter.name}
                  </option>
                ))}
              </select>
              {rosterIds.length > 0 && (
                <label className="inline-flex items-center gap-2 text-sm text-ink-700">
                  <input
                    type="checkbox"
                    checked={showAllShooters}
                    onChange={(e) => setShowAllShooters(e.target.checked)}
                    tabIndex={-1}
                  />
                  Show full directory
                </label>
              )}
            </div>
            {rosterIds.length > 0 && !showAllShooters && (
              <p className="text-xs text-ink-400 mt-2">
                Showing match roster only ({rosterIds.length}). Toggle “Show full
                directory” to pick someone else.
              </p>
            )}
          </div>

          {OCR_ENABLED &&
            formData.shooter_id &&
            Object.keys(scoresByType).length > 0 && (
              <ScorecardScan
                api={API}
                matchId={matchId}
                shooterId={formData.shooter_id}
                onApply={applyOcrResult}
              />
            )}

          {formData.shooter_id && Object.keys(scoresByType).length > 0 && (
            <div className="space-y-8">
              {Object.entries(scoresByType).map(([matchType, scores]) => (
                <div key={matchType} className="card overflow-hidden">
                  <div className="card-header flex-col items-start">
                    <h3 className="section-title">{matchType} Scores</h3>
                    <p className="text-sm text-ink-500 mt-1">
                      Enter scores for all calibers in this match type
                    </p>
                  </div>

                  <div className="card-pad">
                    <div className="border-b border-ink-100 mb-4">
                      <div className="flex overflow-x-auto">
                        {scores.map((score, idx) => (
                          <button
                            key={idx}
                            type="button"
                            tabIndex={-1}
                            onClick={() => {
                              document
                                .getElementById(
                                  `score-${matchType}-${score.caliber}`
                                )
                                ?.scrollIntoView({
                                  behavior: "smooth",
                                  block: "start",
                                });
                            }}
                            className="px-4 py-2 font-medium text-sm whitespace-nowrap text-ink-500 hover:text-brand-700 transition-colors"
                          >
                            {score.caliber}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-8">
                      {scores.map((score) => {
                        const scoreIndex = formData.scores.findIndex(
                          (s) =>
                            s.match_type_instance === score.match_type_instance &&
                            s.caliber === score.caliber
                        );

                        if (scoreIndex === -1) return null;

                        const matchTypeObj = score.matchTypeObj;
                        const maxScore = matchTypeObj ? matchTypeObj.max_score : 0;

                        const { totalScore, totalXCount, allStagesTotalNull } =
                          calculateTotals(formData.scores[scoreIndex]);
                        const subtotals = calculateSubtotals(
                          formData.scores[scoreIndex],
                          matchTypeObj
                        );

                        return (
                          <div
                            key={`${score.match_type_instance}-${score.caliber}`}
                            id={`score-${matchType}-${score.caliber}`}
                            className="rounded-xl border border-ink-200 p-4"
                          >
                            <div className="mb-3 flex items-center gap-2">
                              <h4 className="text-lg font-medium text-ink-900">
                                {score.match_type_instance}
                              </h4>
                              <span className="badge-brand">{score.caliber}</span>
                            </div>

                            {score.caliber !== ".22" && (
                              <label className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 rounded border-ink-300 text-amber-600 focus:ring-amber-500"
                                  tabIndex={-1}
                                  checked={
                                    !!formData.scores[scoreIndex].fired_twentytwo
                                  }
                                  onChange={(e) =>
                                    handleFiredTwentytwoChange(
                                      scoreIndex,
                                      e.target.checked
                                    )
                                  }
                                />
                                <span>
                                  <span className="font-medium">.22 only</span> —
                                  fired a .22 for this {score.caliber} card (scored
                                  separately from true {score.caliber} shooters)
                                </span>
                              </label>
                            )}

                            <div className="mb-6">
                              <h5 className="font-medium mb-3 text-ink-700">
                                Entry Stages
                              </h5>
                              {/*
                                Single-column stack keeps visual order === tab order
                                (Score then X for each stage). Avoid multi-column
                                stage grids which scramble keyboard path.
                              */}
                              <div className="space-y-3 max-w-xl">
                                {formData.scores[scoreIndex].stages.map(
                                  (stage, stageIdx) => {
                                    const scoreTab = navIndex++;
                                    const xTab = navIndex++;
                                    return (
                                      <div
                                        key={stageIdx}
                                        className="rounded-lg border border-ink-200 bg-ink-50/40 p-3 hover:bg-white hover:shadow-card transition-all grid grid-cols-[4rem_1fr_1fr] gap-3 items-end"
                                      >
                                        <div className="font-medium text-sm text-ink-700 pb-2">
                                          {stage.name}
                                        </div>
                                        <div>
                                          <label className="field-label">
                                            Score
                                          </label>
                                          <input
                                            type="number"
                                            inputMode={isMobile ? "none" : "numeric"}
                                            enterKeyHint="next"
                                            readOnly={isMobile}
                                            min="0"
                                            max="100"
                                            data-score-nav="1"
                                            data-score-index={scoreIndex}
                                            data-stage-index={stageIdx}
                                            data-field="score"
                                            tabIndex={scoreTab}
                                            value={
                                              stage.score === null ? "" : stage.score
                                            }
                                            onChange={(e) =>
                                              handleStageChange(
                                                scoreIndex,
                                                stageIdx,
                                                "score",
                                                e.target.value
                                              )
                                            }
                                            onKeyDown={handleScoreKeyDown}
                                            onFocus={handleScoreFocus}
                                            className="input"
                                          />
                                        </div>
                                        <div>
                                          <label className="field-label">
                                            X Count
                                          </label>
                                          <input
                                            type="number"
                                            inputMode={isMobile ? "none" : "numeric"}
                                            enterKeyHint="next"
                                            readOnly={isMobile}
                                            min="0"
                                            max="10"
                                            data-score-nav="1"
                                            data-score-index={scoreIndex}
                                            data-stage-index={stageIdx}
                                            data-field="x_count"
                                            tabIndex={xTab}
                                            value={
                                              stage.x_count === null
                                                ? ""
                                                : stage.x_count
                                            }
                                            onChange={(e) =>
                                              handleStageChange(
                                                scoreIndex,
                                                stageIdx,
                                                "x_count",
                                                e.target.value
                                              )
                                            }
                                            onKeyDown={handleScoreKeyDown}
                                            onFocus={handleScoreFocus}
                                            className="input"
                                          />
                                        </div>
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            </div>

                            {Object.keys(subtotals).length > 0 && (
                              <div className="mb-6">
                                <h5 className="font-medium mb-3 text-ink-700">
                                  Subtotals (Automatically Calculated)
                                </h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                  {Object.entries(subtotals).map(
                                    ([subtotalName, values]) => (
                                      <div
                                        key={subtotalName}
                                        className="rounded-lg border border-ink-200 bg-ink-50 p-3"
                                      >
                                        <h5 className="font-medium text-ink-800 mb-2">
                                          {subtotalName}
                                        </h5>
                                        <div className="grid grid-cols-2 gap-3">
                                          <div>
                                            <label className="field-label">
                                              Score
                                            </label>
                                            <div className="w-full rounded-lg border border-ink-200 bg-white px-3 py-1 font-medium text-ink-900">
                                              {values.score}
                                            </div>
                                          </div>
                                          <div>
                                            <label className="field-label">
                                              X Count
                                            </label>
                                            <div className="w-full rounded-lg border border-ink-200 bg-white px-3 py-1 font-medium text-ink-900">
                                              {values.x_count}
                                            </div>
                                          </div>
                                        </div>
                                        {matchTypeObj.subtotal_mappings[
                                          subtotalName
                                        ] && (
                                          <div className="mt-2 text-xs text-ink-400">
                                            Sum of:{" "}
                                            {matchTypeObj.subtotal_mappings[
                                              subtotalName
                                            ].join(", ")}
                                          </div>
                                        )}
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}

                            <div className="mt-4 rounded-xl border border-brand-100 bg-brand-50/60 p-4">
                              <h5 className="font-medium text-ink-800 mb-2">Total</h5>
                              <div className="flex justify-between items-center">
                                {totalScore === null || allStagesTotalNull ? (
                                  <div>
                                    <span className="text-lg font-semibold text-ink-900">-</span>
                                    <span className="ml-4 text-ink-500">
                                      X Count: -
                                    </span>
                                  </div>
                                ) : (
                                  <>
                                    <div>
                                      <span className="text-lg font-semibold text-brand-700">
                                        {totalScore}
                                      </span>
                                      <span className="text-ink-500">
                                        {" "}
                                        / {maxScore}
                                      </span>
                                      <span className="ml-4 text-ink-500">
                                        X Count: {totalXCount}
                                      </span>
                                    </div>
                                    <span className="badge-brand">
                                      {maxScore > 0
                                        ? Math.round((totalScore / maxScore) * 100)
                                        : 0}
                                      %
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}

              {/* Desktop save; mobile uses the sticky bottom bar below. */}
              <div className="hidden sm:flex justify-end mt-8">
                <button
                  type="submit"
                  disabled={saving}
                  tabIndex={navIndex}
                  className="btn-primary"
                >
                  {saving ? "Saving..." : "Save All Scores"}
                </button>
              </div>
            </div>
          )}

          {formData.shooter_id && Object.keys(scoresByType).length === 0 && (
            <div className="text-center p-8 rounded-xl border border-dashed border-ink-200 bg-ink-50/50">
              <p className="text-ink-500">
                No match types configured for this match. Please update the match
                configuration.
              </p>
            </div>
          )}

          {!formData.shooter_id && (
            <div className="text-center p-8 rounded-xl border border-dashed border-ink-200 bg-ink-50/50">
              <p className="text-ink-500">
                Please select a shooter to enter scores.
              </p>
            </div>
          )}

          {/* Mobile-only: docked keypad while editing, else a sticky Save + running-total bar. */}
          {isMobile &&
            formData.shooter_id &&
            Object.keys(scoresByType).length > 0 && (
              <>
                {/* Spacer so the fixed bar/keypad never covers the last card. */}
                <div className="h-40 sm:hidden" aria-hidden="true" />
                {activeField ? (
                  entryMode === "tap" ? (
                    <TapPad
                      context={keypadContext()}
                      shots={stageShots()}
                      onShot={tapShot}
                      onUndo={tapUndo}
                      onNext={advanceToNextStage}
                      onDone={() => setActiveField(null)}
                      onSwitchMode={() => setMode("keypad")}
                    />
                  ) : (
                    <NumericKeypad
                      context={keypadContext()}
                      fieldName={activeField.field === "score" ? "Score" : "X Count"}
                      value={activeStageValue()}
                      max={FIELD_MAX[activeField.field] ?? 100}
                      quickLabel={activeField.field === "score" ? "100" : "10"}
                      onDigit={keypadDigit}
                      onQuick={keypadQuick}
                      onBackspace={keypadBackspace}
                      onClear={() => commitActive(null)}
                      onNext={advanceFromActive}
                      onDone={() => setActiveField(null)}
                      onSwitchMode={() => setMode("tap")}
                    />
                  )
                ) : (
                  <div className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-3 border-t border-ink-200 bg-white/95 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-pop backdrop-blur sm:hidden">
                    <div className="text-sm leading-tight">
                      <div className="text-ink-500">Running total</div>
                      <div className="text-lg font-bold text-ink-900">
                        {grandTotal.score}
                        <span className="ml-1 text-sm font-medium text-ink-500">
                          ({grandTotal.x}X)
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* A/B entry-mode toggle: pick the arm to test on this match. */}
                      <div
                        className="inline-flex rounded-lg bg-ink-100 p-0.5 text-xs font-semibold"
                        role="group"
                        aria-label="Score entry mode"
                      >
                        <button
                          type="button"
                          onClick={() => setMode("keypad")}
                          aria-pressed={entryMode === "keypad"}
                          className={`rounded-md px-2.5 py-1 transition-colors ${
                            entryMode === "keypad"
                              ? "bg-white text-brand-700 shadow-sm"
                              : "text-ink-500"
                          }`}
                        >
                          Keypad
                        </button>
                        <button
                          type="button"
                          onClick={() => setMode("tap")}
                          aria-pressed={entryMode === "tap"}
                          className={`rounded-md px-2.5 py-1 transition-colors ${
                            entryMode === "tap"
                              ? "bg-white text-brand-700 shadow-sm"
                              : "text-ink-500"
                          }`}
                        >
                          Tap
                        </button>
                      </div>
                      <button type="submit" disabled={saving} className="btn-primary">
                        {saving ? "Saving..." : "Save all"}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
        </form>
      </div>
    </div>
  );
};

export default ScoreEntry;
